
import { supabase } from '@/integrations/supabase/client';
import { securityLogger } from './securityLogger';

export const enhancedSecurityService = {
  // Enhanced rate limiting using existing database function
  async checkRateLimit(action: string, maxAttempts: number = 5, windowMinutes: number = 15): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase.rpc('check_rate_limit_enhanced', {
        _user_id: user.id,
        _action: action,
        _max_attempts: maxAttempts,
        _window_minutes: windowMinutes
      });

      if (error) {
        return true; // Allow on error to avoid blocking legitimate users
      }

      return data as boolean;
    } catch (error) {
      return true; // Allow on error
    }
  },

  // Enhanced input sanitization with validation
  sanitizeAndValidateInput(input: string, context: string = 'general'): string {
    if (!input || typeof input !== 'string') return '';
    
    try {
      // Use client-side validation first for immediate feedback
      const maxLengths = {
        name: 100,
        email: 254,
        description: 500,
        instruction: 2000,
        organization_name: 100,
        general: 1000
      };

      const maxLength = maxLengths[context as keyof typeof maxLengths] || 1000;
      
      // Enhanced sanitization
      let sanitized = input
        .replace(/[<>]/g, '') // Remove angle brackets
        .replace(/javascript:/gi, '') // Remove javascript: protocol
        .replace(/on\w+=/gi, '') // Remove event handlers
        .replace(/data:/gi, '') // Remove data: protocol
        .replace(/vbscript:/gi, '') // Remove vbscript: protocol
        .trim();

      // Limit length
      if (sanitized.length > maxLength) {
        sanitized = sanitized.slice(0, maxLength);
      }

      // Context-specific validation
      if (context === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sanitized)) {
        throw new Error('Invalid email format');
      }

      if ((context === 'name' || context === 'organization_name') && sanitized.length < 2) {
        throw new Error(`${context.replace('_', ' ')} must be at least 2 characters`);
      }

      return sanitized;
    } catch (error) {
      throw error;
    }
  },

  // Validate organization access for sensitive operations
  async validateOrganizationAccess(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', userId)
        .single();

      if (error || !data?.organization_id) {
        await securityLogger.logSecurityEvent('organization_access_denied', {
          user_id: userId,
          reason: 'no_organization'
        });
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  },

  // Enhanced security event logging with context
  async logSecurityEvent(eventType: string, details: any = {}): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const enhancedDetails = {
        ...details,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        url: window.location.href,
        referrer: document.referrer
      };

      const { error } = await supabase.rpc('log_policy_violation', {
        _table_name: 'security_events',
        _operation: eventType,
        _details: enhancedDetails
      });

      if (error) {
        // Silent fail - don't expose logging errors
      }
    } catch (error) {
      // Silent fail - don't expose logging errors
    }
  },

  // Validate admin operations with enhanced logging
  async validateAdminOperation(operation: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      // Check if user has admin role
      const { data, error } = await supabase.rpc('has_role', {
        _user_id: user.id,
        _role: 'admin'
      });

      if (error) {
        return false;
      }

      // Log the operation attempt
      await this.logSecurityEvent(
        data ? 'admin_operation_authorized' : 'admin_operation_denied',
        { operation, user_id: user.id }
      );

      return data as boolean;
    } catch (error) {
      return false;
    }
  },

  // Password strength validation (client-side)
  validatePasswordStrength(password: string): boolean {
    // Minimum 8 characters
    if (password.length < 8) return false;
    
    // Must contain uppercase letter
    if (!/[A-Z]/.test(password)) return false;
    
    // Must contain lowercase letter
    if (!/[a-z]/.test(password)) return false;
    
    // Must contain number
    if (!/[0-9]/.test(password)) return false;
    
    // Must contain special character
    if (!/[^A-Za-z0-9]/.test(password)) return false;
    
    return true;
  },

  // Run security health checks using existing functions
  async runSecurityHealthCheck(): Promise<any[]> {
    try {
      const checks = [];

      // Check for policy conflicts
      const { data: policyData, error: policyError } = await supabase.rpc('check_policy_conflicts');
      
      if (!policyError && policyData) {
        checks.push({
          check_name: 'policy_conflicts',
          status: policyData.some((p: any) => p.has_permissive_policies) ? 'WARN' : 'PASS',
          details: `${policyData.length} tables checked for policy conflicts`
        });
      }

      return checks;
    } catch (error) {
      return [];
    }
  },

  // Detect policy conflicts using existing function
  async detectPolicyConflicts(): Promise<any[]> {
    try {
      const { data, error } = await supabase.rpc('check_policy_conflicts');

      if (error) {
        return [];
      }

      return data || [];
    } catch (error) {
      return [];
    }
  }
};
